import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Backdrop, Box, Button, Card, CardContent, Grid, Typography } from "@material-ui/core";
import { GridRowModel } from "@material-ui/x-grid";
import { useDispatch } from "react-redux";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import CommonLoader from "src/components/Loader/CommonLoader";
import TabHeader from "src/components/TabHeader/TabHeader";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { useStyles } from "./style";

interface Props {}

function normalizeRows(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.pendingExaminations)) return payload.pendingExaminations;
  if (Array.isArray(payload?.value)) return payload.value;
  if (Array.isArray(payload?.value?.pendingExaminations)) return payload.value.pendingExaminations;
  return [];
}

export function PendingExaminations({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useDispatch();

  const moduleName = "Pending Examinations";
  const pageHeading = "Pending Examinations";

  const [pendingExaminations, setPendingExaminations] = useState<GridRowModel[]>([]);
  const [loader, setLoader] = useState(false);

  interface ActionCellProps {
    row: {
      original: any;
    };
  }

  const handleSend = useCallback(
    async (row: any) => {
      const id = row?.id;
      if (!id) {
        dispatch(
          addNotificationAction({
            message: "Missing record id.",
            options: { variant: "warning" },
          })
        );
        return;
      }

      setLoader(true);
      try {
        const results: any = await fetchWrapper.put(`update-ihc-src-sent/${id}`, {});

        if (results?.isSuccess === false) {
          dispatch(
            addNotificationAction({
              message: results?.message || "Failed to send.",
              options: { variant: "error" },
            })
          );
          return;
        }

        dispatch(
          addNotificationAction({
            message: results?.message || "Sent successfully.",
            options: { variant: "success" },
          })
        );

        const refreshed: any = await fetchWrapper.get("get-pending-examinations");
        setPendingExaminations(normalizeRows(refreshed?.value ?? refreshed) as GridRowModel[]);
      } catch (error: any) {
        dispatch(
          addNotificationAction({
            message: error?.message || "Failed to send.",
            options: { variant: "error" },
          })
        );
      } finally {
        setLoader(false);
      }
    },
    [dispatch]
  );

  const ActionCell = useCallback(
    ({ row }: ActionCellProps) => {
      const original = row?.original;

      return (
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleSend(original);
          }}
        >
          Send
        </Button>
      );
    },
    [handleSend]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "srNo",
        header: "Sr.No",
        size: 70,
        accessorFn: (_row: any, index: number) => index + 1,
      },
      {
        accessorKey: "virNo",
        header: "VIR #",
        size: 220,
        accessorFn: (row: any) => row?.virNo || " ",
      },
      {
        accessorKey: "containerNumber",
        header: "Container #",
        size: 200,
        accessorFn: (row: any) => row?.containerNumber || " ",
      },
      {
        accessorKey: "blNo",
        header: "BL #",
        size: 180,
        accessorFn: (row: any) => row?.blNo || " ",
      },
      {
        accessorKey: "handlingCode",
        header: "Handling Code",
        size: 140,
        accessorFn: (row: any) => row?.handlingCode || " ",
      },
      {
        accessorKey: "executionOrder",
        header: "Execution Order",
        size: 140,
        accessorFn: (row: any) => row?.executionOrder ?? " ",
      },
      {
        accessorKey: "performed",
        header: "Performed",
        size: 200,
        accessorFn: (row: any) => row?.performed || " ",
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        size: 200,
        accessorFn: (row: any) => row?.createdAt || " ",
      },
      // {
      //   accessorKey: "terminalId",
      //   header: "Terminal",
      //   size: 110,
      //   accessorFn: (row: any) => row?.terminalId ?? " ",
      // },
      // {
      //   accessorKey: "gatePassId",
      //   header: "Gate Pass ID",
      //   size: 140,
      //   accessorFn: (row: any) => row?.gatePassId ?? " ",
      // },
      {
        accessorKey: "indexNo",
        header: "Index #",
        size: 110,
        accessorFn: (row: any) => row?.indexNo ?? " ",
      },
      // {
      //   accessorKey: "isCleared",
      //   header: "Cleared",
      //   size: 110,
      //   accessorFn: (row: any) => (row?.isCleared === 1 ? "Yes" : "No"),
      // },
      // {
      //   accessorKey: "srcSent",
      //   header: "SRC Sent",
      //   size: 120,
      //   accessorFn: (row: any) => (row?.srcSent === 1 ? "Yes" : "No"),
      // },
      {
        accessorKey: "action",
        header: "Action",
        size: 130,
        Cell: ActionCell,
      },
    ],
    [ActionCell]
  );

  const fetchPendingExaminations = useCallback(async () => {
    setLoader(true);
    try {
      const results: any = await fetchWrapper.get("get-pending-examinations");
      setPendingExaminations(normalizeRows(results?.value ?? results) as GridRowModel[]);
    } catch (error: any) {
      dispatch(
        addNotificationAction({
          message: error?.message || "Failed to fetch pending examinations.",
          options: { variant: "error" },
        })
      );
      setPendingExaminations([]);
    } finally {
      setLoader(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchPendingExaminations();
  }, [fetchPendingExaminations]);

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>

      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2}>
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
              </Box>
            </Grid>
          </Grid>
          <CommonMaterialGrid
            rows={pendingExaminations}
            columns={columns}
            defaultPerPageRecord={50}
            loader={loader}
            height={600}
            isFooter={true}
            hideFooter={true}
            filename={"Pending Examinations"}
          />
        </CardContent>
      </Card>
    </>
  );
}
